import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { PdfPreview } from "./PdfPreview";
import { usePresentation } from "@/zapi-frontend/shared/hooks/usePresentation";
import { PresentationInstructionsModal } from "./PresentationInstructionsModal";
import { formatGeneratedTime } from "../lib/formatGeneratedTime";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { LeoChat } from "@/zapi-frontend/widgets/LeoChat";
import { useLeoChatStore } from "@/zapi-frontend/stores/leoChatStore";
import { ChatPanelLoading, DockedChatIsland } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { useChatUnreadIndicator } from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { PresentationBuilder, type TDeckState } from "./PresentationBuilder";

export function PresentationPage({ chatSlot }: { chatSlot: HTMLElement | null }) {
  const { t } = useTranslation("projectHome");
  const {
    isLoading,
    isSlidesGenerating,
    isSlidesRegenerating,
    isGammaGenerating,
    gammaPdfUrl,
    gammaGeneratedAt,
    hasSlides,
    generateSlides,
    regenerateSlides,
    generateGammaPreview
  } = usePresentation();
  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { isLabs, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { id: projectId = "" } = useParams();
  // A Claude-generated project (no template) has Leo as its chat, and Leo keeps its deck in step
  // with the analysis. A templated project's Gamma deck has nothing a chat could change, and is
  // refreshed by hand.
  const isLeoActive = selectedProject?.projectTemplateSlug === "";
  const leoAssistantCount = useLeoChatStore(
    (s) => (s.historyByProject[projectId] ?? []).filter((m) => m.role === "assistant").length
  );
  const { hasUnread: hasUnreadChat, markSeen: markChatSeen } = useChatUnreadIndicator(leoAssistantCount, {
    firstVisitKey: projectId ? `slides:${projectId}` : undefined,
    conversationKey: `leo:${projectId}`
  });

  const [previewPageIndex, setPreviewPageIndex] = useState(1);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);

  const isPageLoading = isLoading || isFetchProjectLoading;
  // Leo rebuilds a Claude deck in place, so the deck on screen stays up meanwhile.
  const isDeckUpdating = isLeoActive && isGammaGenerating && !!gammaPdfUrl;
  const deckState: TDeckState =
    isSlidesGenerating || isSlidesRegenerating || (isGammaGenerating && !isDeckUpdating)
      ? "generating"
      : isPageLoading
      ? "loading"
      : gammaPdfUrl
      ? "ready"
      : "empty";

  return (
    <>
      <PresentationBuilder
        deckState={deckState}
        deckUpdating={isDeckUpdating}
        deckMeta={formatGeneratedTime(gammaGeneratedAt, "created", t)}
        canEdit={hasEdit}
        showInstructions={!!isLabs}
        onOpenInstructions={() => setIsInstructionsOpen(true)}
        // An outline left without its deck (a deck that failed) only needs the deck.
        onCreateSlides={hasSlides ? generateGammaPreview : generateSlides}
        onRefreshSlides={isLeoActive ? null : regenerateSlides}
        preview={
          <PdfPreview
            pdfUrl={gammaPdfUrl}
            initialPage={previewPageIndex}
            onPageChange={setPreviewPageIndex}
            isPageLoading={isPageLoading}
          />
        }
      />
      {chatSlot &&
        isLeoActive &&
        createPortal(
          <DockedChatIsland
            appearance="design-system"
            hasUnread={hasUnreadChat}
            onOpenChange={markChatSeen}
            floatingClassName="fixed right-2 top-[calc(var(--app-header-height)+0.5rem)] bottom-2"
          >
            {String(selectedProject?.id) !== projectId ? (
              <ChatPanelLoading />
            ) : (
              <LeoChat projectId={projectId} slideIndex={deckState === "ready" ? previewPageIndex - 1 : undefined} />
            )}
          </DockedChatIsland>,
          chatSlot
        )}
      <PresentationInstructionsModal open={isInstructionsOpen} onClose={() => setIsInstructionsOpen(false)} />
    </>
  );
}
